/**
 * Abstract base class for all handles.
 *
 * Provides common functionality shared by transform handles and action handles:
 * - Hit testing
 * - Entity handle extraction
 * - Common properties (viewer, history, module, worldPosition, drawOptions)
 */

import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { DrawingModuleType } from "../../DrawingSingleton";
import type { DrawingModule } from "../../types/DrawingWeb";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { IHandle } from "./IHandle";
import type { TransformContext, HandleDrawOptions, Point3D } from "../types";
import {
  HandleType,
  HandlePosition,
  DEFAULT_HANDLE_DRAW_OPTIONS,
} from "../types";
import { getEntityHandle } from "../../core/oda";
import { distance2D } from "../matrix";
import { Logger } from "../../../logger";

/**
 * Abstract base for all handle types.
 */
export abstract class AbstractHandle implements IHandle {
  abstract readonly type: HandleType;
  abstract readonly position: HandlePosition;

  protected viewer: DRDrawingViewer;
  protected history: CommandHistory;
  protected module: DrawingModuleType;

  /** World position of this handle */
  worldPosition: Point3D;

  /** by default, handles does not accept ortho or polar mode. */
  snapOptions = {
    acceptOrtho: false,
    acceptPolar: false,
    acceptObject: true,
  };

  /** Draw options */
  drawOptions: HandleDrawOptions = { ...DEFAULT_HANDLE_DRAW_OPTIONS };

  /** Whether an operation is in progress */
  protected isTransforming = false;

  constructor(viewer: DRDrawingViewer, history: CommandHistory) {
    this.viewer = viewer;
    this.history = history;
    this.module = viewer.module;
    this.worldPosition = { x: 0, y: 0, z: 0 };
  }

  /**
   * Hit test - check if point is within handle radius.
   */
  hitTest(worldPoint: Point3D, tolerance: number): boolean {
    const dist = distance2D(worldPoint, this.worldPosition);
    return dist <= tolerance;
  }

  /**
   * Start operation.
   */
  abstract start(context: TransformContext): void;

  /**
   * Ongoing operation.
   */
  abstract ongoing(context: TransformContext): void;

  /**
   * End operation.
   */
  abstract end(context: TransformContext): void;

  /**
   * Cancel operation.
   */
  abstract cancel(): void;

  /**
   * Dispose resources.
   */
  dispose(): void {
    if (this.isTransforming) {
      this.cancel();
    }
  }

  beforeRedraw() {}

  // ============== Protected Helper Methods ==============

  /**
   * Get entity handle string.
   */
  protected getEntityHandle(entityId: DrawingModule.OdDbObjectId): string {
    return getEntityHandle(entityId);
  }

  /**
   * Call viewer.update() with a Sentry breadcrumb and a try-catch.
   * Use this in end()/cancel() paths that follow view.erase(ghost) so that if
   * a WASM use-after-free crash occurs, Sentry receives structured context.
   */
  protected safeViewerUpdate(
    source: string,
    extra?: Record<string, unknown>,
  ): void {
    Logger.breadcrumb(`viewer.update — ${source}`, extra);
    try {
      this.viewer.update();
    } catch (e) {
      Logger.captureException(e, { source, ...extra });
      throw e;
    }
  }
}
